import axiosInstance from './axiosInstance'
import axios from 'axios'

// ── Field Types ───────────────────────────────────────────────────────────────

export const FIELD_TYPES = [
  'text', 'textarea', 'number', 'email', 'phone', 'date',
  'dropdown', 'radio', 'checkbox', 'multi_select', 'url',
] as const
export type FieldType = typeof FIELD_TYPES[number]

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text:         'Text',
  textarea:     'Textarea',
  number:       'Number',
  email:        'Email',
  phone:        'Phone',
  date:         'Date',
  dropdown:     'Dropdown',
  radio:        'Radio',
  checkbox:     'Checkbox',
  multi_select: 'Multi Select',
  url:          'URL',
}

// ── Default system fields ─────────────────────────────────────────────────────
// Only fields that exist as columns in the leads table are allowed as mapping targets.

export const DEFAULT_LEAD_FIELDS: DefaultLeadField[] = [
  { key: 'name',             label: 'Full Name',             mappingField: 'name',             required: false, visible: true,  fieldType: 'text'  },
  { key: 'phone',            label: 'Phone',                 mappingField: 'phone',            required: true,  visible: true,  fieldType: 'phone' },
  { key: 'email',            label: 'Email',                 mappingField: 'email',            required: false, visible: true,  fieldType: 'email' },
  // source / source_ad_name / source_campaign / source_medium are NOT user-fillable fields.
  // They are set at the form level (Settings tab) and applied internally to every lead
  // created by that form. Source is always "custom_form".
  { key: 'bestTimeToContact',label: 'Best Time to Contact',  mappingField: 'bestTimeToContact',required: false, visible: false, fieldType: 'text'  },
  { key: 'requirements',     label: 'Requirements',          mappingField: 'requirements',     required: false, visible: false, fieldType: 'textarea' },
  { key: 'description',      label: 'Description',           mappingField: 'nextAction',       required: false, visible: false, fieldType: 'textarea' },
  { key: 'company_name',     label: 'Company Name',          mappingField: 'companyName',      required: false, visible: false, fieldType: 'text'  },
  { key: 'designation',      label: 'Designation',           mappingField: 'designation',      required: false, visible: false, fieldType: 'text'  },
  { key: 'website_url',       label: 'Website URL',           mappingField: 'websiteUrl',       required: false, visible: false, fieldType: 'url'   },
  { key: 'industry_type',    label: 'Industry',              mappingField: 'industryType',     required: false, visible: false, fieldType: 'text'  },
  { key: 'address',          label: 'Address',               mappingField: 'address',          required: false, visible: false, fieldType: 'textarea' },
  { key: 'gst',              label: 'GST Number',            mappingField: 'gst',              required: false, visible: false, fieldType: 'text'  },
  { key: 'pincode',          label: 'Pincode',               mappingField: 'pincode',          required: false, visible: false, fieldType: 'text'  },
]

// Valid lead table column mapping targets (used in FieldEditor mapping dropdown)
export const LEAD_MAPPING_OPTIONS = DEFAULT_LEAD_FIELDS.map(f => ({
  label: f.label,
  value: f.mappingField,
}))

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface DefaultLeadField {
  key:          string
  label:        string
  mappingField: string
  required:     boolean
  visible:      boolean
  fieldType:    FieldType
}

export interface FieldOption {
  label: string
  value: string
}

export interface FieldConfiguration {
  options?:          FieldOption[]
  validationRules?:  string
  defaultValue?:     string
  minLength?:        number
  maxLength?:        number
  min?:              number
  max?:              number
}

export interface LeadFormField {
  id:               string
  formId:           string
  fieldType:        FieldType
  fieldName:        string
  label:            string
  placeholder?:     string | null
  required:         boolean
  displayOrder:     number
  mappingField?:    string | null
  configuration:    FieldConfiguration
  isDefault:        boolean
  isVisible:        boolean
}

export interface LeadForm {
  id:             string
  name:           string
  slug:           string
  description?:   string | null
  organizationId?: string | null
  orgName?:       string | null
  lobId?:         string | null
  lobName?:       string | null
  warehouseId?:   string | null
  warehouseName?: string | null
  // Tracking — set at form level, applied to every lead automatically
  source?:          string | null
  sourceAdName?:    string | null
  sourceCampaign?:  string | null
  sourceMedium?:    string | null
  // Branding — shown in the public form header
  formCompanyName?: string | null
  formLogoUrl?:  string | null
  successMessage: string
  redirectUrl?:   string | null
  status:         'Active' | 'Inactive'
  fields:         LeadFormField[]
  totalSubmissions: number
  createdBy?:     { id: string; name: string; email: string } | null
  createdAt:      string
  updatedAt:      string
}

export interface LeadFormSummary {
  id:               string
  name:             string
  slug:             string
  description?:     string | null
  orgName?:         string | null
  lobName?:         string | null
  warehouseName?:   string | null
  status:           'Active' | 'Inactive'
  totalSubmissions: number
  createdBy?:       { id: string; name: string } | null
  createdAt:        string
}

export interface CreateLeadFormFieldPayload {
  fieldType:     FieldType
  fieldName:     string
  label:         string
  placeholder?:  string
  required:      boolean
  displayOrder:  number
  mappingField?: string
  isDefault:     boolean
  isVisible:     boolean
  configuration: FieldConfiguration
}

export interface CreateLeadFormPayload {
  name:           string
  description?:   string
  organizationId?: string
  lobId?:         string
  warehouseId?:   string
  // Tracking fields
  sourceAdName?:    string
  sourceCampaign?:  string
  sourceMedium?:    string
  // Branding
  formCompanyName?: string
  formLogoUrl?:  string
  successMessage: string
  redirectUrl?:   string
  status:         'Active' | 'Inactive'
  fields:         CreateLeadFormFieldPayload[]
}

export interface UpdateLeadFormPayload extends Partial<CreateLeadFormPayload> {}

export interface FormSubmissionResponse {
  id:            string
  formId:        string
  leadId:        string
  leadName:      string
  leadEmail?:    string | null
  leadPhone?:    string | null
  formName:      string
  submissionData: Record<string, any>
  ipAddress?:    string | null
  utmSource?:    string | null
  utmMedium?:    string | null
  utmCampaign?:  string | null
  createdAt:     string
}

export interface PublicFormData {
  id:             string
  name:           string
  slug:           string
  description?:   string | null
  formCompanyName?: string | null
  formLogoUrl?:  string | null
  successMessage: string
  redirectUrl?:   string | null
  status:         'Active' | 'Inactive'
  fields:         LeadFormField[]
}

export interface PublicSubmitPayload {
  formData:     Record<string, any>
  utmSource?:   string
  utmMedium?:   string
  utmCampaign?: string
  utmContent?:  string
  utmTerm?:     string
}

export interface LeadFormFilters {
  search?:  string
  status?:  'Active' | 'Inactive'
  page?:    number
  pageSize?: number
}

export interface PaginatedForms {
  items:      LeadFormSummary[]
  total:      number
  page:       number
  limit:      number
  totalPages: number
}

// ── Admin API ─────────────────────────────────────────────────────────────────

export const leadFormsApi = {

  getAll: async (filters: LeadFormFilters = {}): Promise<PaginatedForms> => {
    const res = await axiosInstance.get<{ data: any }>('/leadforms', { params: filters })
    const raw = res.data.data
    return {
      items:      raw.items      ?? raw.results     ?? [],
      total:      raw.total      ?? raw.rowCount     ?? 0,
      page:       raw.page       ?? raw.currentPage  ?? 1,
      limit:      raw.limit      ?? raw.pageSize     ?? 20,
      totalPages: raw.totalPages ?? 1,
    }
  },

  getById: async (id: string): Promise<LeadForm> => {
    const res = await axiosInstance.get<{ data: LeadForm }>(`/leadforms/${id}`)
    return res.data.data
  },

  create: async (payload: CreateLeadFormPayload): Promise<LeadForm> => {
    const res = await axiosInstance.post<{ data: LeadForm }>('/leadforms', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateLeadFormPayload): Promise<LeadForm> => {
    const res = await axiosInstance.put<{ data: LeadForm }>(`/leadforms/${id}`, payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/leadforms/${id}`)
  },

  duplicate: async (id: string): Promise<LeadForm> => {
    const res = await axiosInstance.post<{ data: LeadForm }>(`/leadforms/${id}/duplicate`)
    return res.data.data
  },

  toggleStatus: async (id: string, status: 'Active' | 'Inactive'): Promise<LeadForm> => {
    const res = await axiosInstance.patch<{ data: LeadForm }>(`/leadforms/${id}/status`, { status })
    return res.data.data
  },

  getResponses: async (id: string, params?: { page?: number; pageSize?: number }): Promise<{ items: FormSubmissionResponse[]; total: number; totalPages: number }> => {
    const res = await axiosInstance.get<{ data: any }>(`/leadforms/${id}/responses`, { params })
    const raw = res.data.data
    return {
      items:      raw.items  ?? raw.results ?? [],
      total:      raw.total  ?? raw.rowCount ?? 0,
      totalPages: raw.totalPages ?? 1,
    }
  },

  uploadLogo: async (file: File): Promise<string> => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await axiosInstance.post<{ data: { logoUrl: string } }>('/leadforms/upload-logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data.data.logoUrl
  },
}

// ── Public API (no auth required) ────────────────────────────────────────────

const publicBaseURL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '/api/v1'

const publicAxios = axios.create({
  baseURL: publicBaseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

export const publicFormsApi = {
  getForm: async (slug: string): Promise<PublicFormData> => {
    const res = await publicAxios.get<{ data: PublicFormData }>(`/public/forms/${slug}`)
    return res.data.data
  },

  submit: async (slug: string, payload: PublicSubmitPayload): Promise<{ leadId: string; message: string }> => {
    const res = await publicAxios.post<{ data: { leadId: string; message: string } }>(
      `/public/forms/${slug}/submit`,
      payload,
    )
    return res.data.data
  },
}
